// kiron/quiron/frontend/src/components/LibraryManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// A API de criação agora será usada no modal, mas mantemos as outras
import { getLibraries, deleteLibrary } from '../api'; 
import { useNotification } from '../contexts/NotificationContext';
import styles from './LibraryManager.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// O ícone de 'plus' será usado no novo botão
import { faPlus, faTrash, faBook } from '@fortawesome/free-solid-svg-icons'; 
import ConfirmationModal from './ConfirmationModal';
import InlineLoading from './InlineLoading';
import LibraryDetail from './LibraryDetail';
import LibraryChat from './LibraryChat';
import CreateLibraryModal from './CreateLibraryModal'; // <-- IMPORTAR O NOVO MODAL

const LibraryManager = () => {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [libraries, setLibraries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [libraryToDelete, setLibraryToDelete] = useState(null);
  const [viewingLibrary, setViewingLibrary] = useState(null);
  const [chattingInLibrary, setChattingInLibrary] = useState(null);
  
  // Novo estado para controlar o modal de criação
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const fetchLibraries = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getLibraries();
      setLibraries(data);
    } catch (error) {
      addNotification(t('errorLoadingLibraries'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [addNotification, t]);

  useEffect(() => {
    if (!viewingLibrary && !chattingInLibrary) {
        fetchLibraries();
    }
  }, [fetchLibraries, viewingLibrary, chattingInLibrary]);

  const handleDeleteClick = (library) => {
    setLibraryToDelete(library);
  };

  const confirmDelete = async () => {
    if (!libraryToDelete) return;
    try {
      await deleteLibrary(libraryToDelete.id);
      addNotification(t('libraryDeletedSuccess'), 'success');
      setLibraryToDelete(null);
      fetchLibraries();
    } catch (error) {
      addNotification(error.message || t('errorDeletingLibrary'), 'error');
      setLibraryToDelete(null);
    }
  };

  // Renderização condicional permanece a mesma...
  if (chattingInLibrary) {
    return (
      <LibraryChat
        library={chattingInLibrary}
        onBack={() => setChattingInLibrary(null)}
      />
    );
  }

  if (viewingLibrary) {
    return (
      <LibraryDetail
        library={viewingLibrary}
        onBack={() => setViewingLibrary(null)}
        onStartChat={(lib) => {
            setViewingLibrary(null);
            setChattingInLibrary(lib);
        }}
      />
    );
  }

  // Visão padrão (grade de bibliotecas)
  return (
    <div className={styles.managerContainer}>
      {/* O formulário foi substituído por este container e botão */}
      <div className={styles.formContainer}>
        <button 
          className={styles.addLibraryButton} 
          onClick={() => setIsCreateModalOpen(true)}
          title={t('createNewLibrary')}
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      {isLoading ? (
        <InlineLoading text={t('loadingLibraries')} />
      ) : (
        <div className={styles.libraryGrid}>
          {libraries.length > 0 ? (
            libraries.map((lib) => (
              <div key={lib.id} className={styles.libraryCard} onClick={() => setViewingLibrary(lib)}>
                <FontAwesomeIcon icon={faBook} className={styles.libraryIcon} />
                <span className={styles.libraryName}>{lib.name}</span>
                <button
                  className={styles.deleteButton}
                  onClick={(e) => { e.stopPropagation(); handleDeleteClick(lib); }}
                  title={t('deleteLibrary')}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))
          ) : (
            <p className={styles.noLibrariesMessage}>{t('noLibrariesFound')}</p>
          )}
        </div>
      )}

      {/* Renderiza os modais no final */}
      <CreateLibraryModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onLibraryCreated={fetchLibraries}
      />

      <ConfirmationModal
        isOpen={!!libraryToDelete}
        onClose={() => setLibraryToDelete(null)}
        onConfirm={confirmDelete}
        title={t('deleteLibraryConfirmationTitle')}
        message={t('deleteLibraryConfirmationMessage', { name: libraryToDelete?.name })}
        confirmButtonText={t('delete')}
        cancelButtonText={t('cancel')}
      />
    </div>
  );
};

export default LibraryManager;